import type { ReactNode } from 'react';
import styles from './Ask.module.scss';

/**
 * Renders the small markdown subset the assistant is told to produce: paragraphs, fenced code, pipe tables,
 * bullet and numbered lists, bold and inline code. No external markdown dependency; anything else is shown as text.
 */

const inline = (text: string): ReactNode[] => {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).filter(Boolean);
  return parts.map((part, index) => {
    const key = `${index}-${part}`;
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={key}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={key}>{part.slice(1, -1)}</code>;
    }
    return <span key={key}>{part}</span>;
  });
};

const tableBlock = (lines: string[], key: number): ReactNode => {
  const rows = lines
    .filter((line) => !/^\|?\s*:?-{2,}/.test(line))
    .map((line) =>
      line
        .replace(/^\||\|$/g, '')
        .split('|')
        .map((cell) => cell.trim()),
    );
  const [head, ...body] = rows;
  return (
    <div className={styles.tableWrap} key={key}>
      <table className={styles.table}>
        <thead>
          <tr>
            {head.map((cell, index) => (
              <th key={`${index}-${cell}`}>{inline(cell)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, rowIndex) => (
            <tr key={`${rowIndex}-${row.join('|')}`}>
              {row.map((cell, index) => (
                <td key={`${index}-${cell}`}>{inline(cell)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const listBlock = (lines: string[], key: number, ordered: boolean): ReactNode => {
  const items = lines.map((line) => line.replace(/^\s*(?:[-*]|\d+\.)\s+/, ''));
  if (ordered) {
    return (
      <ol key={key}>
        {items.map((item, index) => (
          <li key={`${index}-${item}`}>{inline(item)}</li>
        ))}
      </ol>
    );
  }
  return (
    <ul key={key}>
      {items.map((item, index) => (
        <li key={`${index}-${item}`}>{inline(item)}</li>
      ))}
    </ul>
  );
};

const textBlock = (block: string, key: number): ReactNode => {
  const lines = block.split('\n');
  if (lines.every((line) => line.trim().startsWith('|'))) {
    return tableBlock(lines, key);
  }
  if (lines.every((line) => /^\s*[-*]\s+/.test(line))) {
    return listBlock(lines, key, false);
  }
  if (lines.every((line) => /^\s*\d+\.\s+/.test(line))) {
    return listBlock(lines, key, true);
  }
  const heading = /^(#{1,3})\s+(.*)$/.exec(block);
  if (heading) {
    return (
      <h3 key={key} className={styles.answerHeading}>
        {inline(heading[2])}
      </h3>
    );
  }
  return <p key={key}>{inline(block)}</p>;
};

export const renderAnswer = (text: string): ReactNode[] => {
  const segments = text.split(/```(\w*)\n([\s\S]*?)```/g);
  const nodes: ReactNode[] = [];
  // split with two capture groups yields [text, lang, code, text, lang, code, ...]
  for (let index = 0; index < segments.length; index += 3) {
    const prose = segments[index];
    prose
      .split(/\n\s*\n/)
      .map((block) => block.trim())
      .filter(Boolean)
      .forEach((block, blockIndex) => nodes.push(textBlock(block, index * 1000 + blockIndex)));
    const code = segments[index + 2];
    if (code !== undefined) {
      nodes.push(
        <pre key={`code-${index}`} className={styles.code}>
          <code>{code.replace(/\n$/, '')}</code>
        </pre>,
      );
    }
  }
  return nodes;
};

export interface CodeBlock {
  lang: string;
  code: string;
}

const isQueryHeading = (line: string): boolean => {
  const bare = line.replace(/[*_#`]/g, '').trim();
  return (
    bare.length <= 50 &&
    /^(sql(\s[^.]*)?|(the\s)?quer(y|ies)(\s(used|run|executed))?)\s*:?$/i.test(bare)
  );
};

/** Separates fenced code from the prose so queries can be shown apart from the answer. */
export const splitAnswer = (text: string): { prose: string; code: CodeBlock[] } => {
  const code: CodeBlock[] = [];
  const prose = text
    .replace(/```(\w*)\n([\s\S]*?)```/g, (_match, lang: string, body: string) => {
      code.push({ lang, code: body.replace(/\n$/, '') });
      return '';
    })
    // Headings that only introduced a removed query ("SQL used", "*The query used:*") go with it.
    .split('\n')
    .filter((line) => !isQueryHeading(line))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return { prose, code };
};

/** Closes an unfinished fence so a partially revealed answer still renders cleanly. */
export const closeFences = (text: string): string => {
  const fences = (text.match(/```/g) ?? []).length;
  return fences % 2 === 1 ? `${text}\n\`\`\`` : text;
};
